<template>
  <b-modal
    id="contact-bulk-modal"
    ref="contact-bulk-modal"
    data-cy="property-contacts-bulk-modal"
    :data-view-ready="true"
    no-close-on-backdrop
    hide-footer
    :title="$t('message.manageBuilding.bulkAction.' + title)">
    <div class="row">
      <div class="col-sm-12 col-md-12">
        <template>
          <label>{{ $t('message.calendar.reminder.contact') }}</label>
          <contact-search-provider :filters="contactSearchFilters">
            <template #default="{ hits, refine }">
              <vue-autosuggest
                ref="autosuggest"
                v-model="name"
                :suggestions="hitsToSuggestions(hits)"
                :input-props="{
                  placeholder: $t('message.generic.search'),
                  class: 'custom-select',
                  id: 'autosuggest__input',
                  'data-cy': 'property-contacts-bulk-search-input',
                }"
                @input="onChange(refine,name)"
                @selected="onSelect">
                <template #default="{ suggestion }">
                  <strong>{{ suggestion.item.name }} {{ suggestion.item.types.length > 0 ? '-' : '' }} {{ suggestion.item.types.map(obj => $t('message.contact.types.' + obj.toUpperCase())).join(', ') }}</strong>
                  <span> <br>{{ suggestion.item.address.street }} {{
                    suggestion.item.address.streetNumber }} <span v-if="(suggestion.item.address.streetNumber && suggestion.item.address.streetNumber !== '') || (suggestion.item.address.street && suggestion.item.address.street !== '')">,</span> {{ suggestion.item.address.zip }} {{
                    suggestion.item.address.city }}</span>
                </template>
              </vue-autosuggest>
            </template>
          </contact-search-provider>
        </template>
      </div>
      <div
        v-if="selectedContacts.length > 0"
        class="col-12">
        <b-table
          bordered
          hover
          responsive="true"
          stacked="md"
          selectable
          select-mode="single"
          class="mb-0 mt-2"
          :fields="fields"
          :items="displayedSelectedContact">
          <template
            #head(contactName)>
            <span>{{ $t('message.interestedParty.reference.contactName') }}</span>
          </template>
          <template
            #head(action)>
            <span>{{ $t('message.calendar.event.action') }}</span>
          </template>
          <template
            #cell(contactName)="data">
            {{ data.item ? data.item.name : '' }}
          </template>
          <template
            #cell(action)="data">
            <coozzy-button
              design="transparent"
              data-cy="property-contacts-bulk-remove-button"
              @click="removeContactFromList(data.item.objectID)">
              <coozzy-delete-icon />
            </coozzy-button>
          </template>
          <template
            #row-details="data">
            <div class="col-12 p-0">
              <coozzy-form-textarea
                ref="note"
                :key="data.item.objectID"
                v-model="data.item.internalNote"
                data-cy="property-contacts-bulk-note-textarea"
                :initial="data.item.internalNote"
                :value-text-area="data.item.internalNote"
                :show-label="false"
                name="note"
                :rows="3"
                :placeholder="$t('message.generic.note')"
                @blur="noteChanged(data.item.id, data.item.internalNote)" />
            </div>
            <template
              v-if="isAddContactBulkTitle && data.item.contactPersons?.length > 0">
              <label class="sizeLabel">{{ $t('message.createOrder.contactPersonOfCompany') }} </label>
              <coozzy-form-select
                v-model="data.item.selectedContactPersonId"
                class="select-entries"
                name="personalContacts"
                @change="contactPersonChanged(data.item, $event)">
                <option :value="null">
                  -
                </option>
                <option
                  v-for="(emp, index) in data.item.contactPersons"
                  :key="index"
                  :value="emp.id">
                  {{ emp.name }}
                </option>
              </coozzy-form-select>
            </template>
          </template>
        </b-table>
      </div>
      <div class="col mt-3">
        <coozzy-button
          size="small"
          class="mb-0 border"
          data-cy="property-contacts-bulk-cancel-button"
          design="transparent"
          @click="$emit('action-cancel');hide()">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          data-cy="property-contacts-bulk-save-button"
          design="green"
          :disabled="disabledBtn || selectedContacts.length === 0"
          @click="addTypeAction">
          <template v-if="['removeJanitors', 'removeCraftsmen', 'removeServiceProviders', 'removeDelegate', 'removeRevisor', 'removeOwner'].includes(title)">
            {{ $t('message.onBoarding.createContactTab.confirmation.title') }}
          </template>
          <template v-else>
            {{ $t('message.generic.form.save') }}
          </template>
        </coozzy-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Vue from 'vue'
import ObjectApi from '@/misc/apis/ObjectApi'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon'
import ContactSearchProvider from '@/framework/components/search/ContactSearchProvider.vue'
import { user } from '@/mixins/user'
import { VueAutosuggest } from 'vue-autosuggest'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'

export default {
  name: 'ObjectContactsBulkAction',
  components: { CoozzyFormTextarea, CoozzyButton, CoozzyDeleteIcon, VueAutosuggest, CoozzyFormSelect, ContactSearchProvider },
  mixins: [user],
  props: {
    title: {
      type: String,
      default: ''
    },
    buildingIds: {
      type: Array,
      default() {
        return []
      }
    },
    ownerIds: {
      type: Array,
      default() {
        return []
      }
    },
    existingContacts: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      disabledBtn: false,
      selectedContacts: [],
      name: '',
      fields: [
        {
          key: 'contactName',
          label: 'contactName',
          thClass: 'table-header',
          tdClass: 'align-middle'
        },
        {
          key: 'action',
          label: 'action',
          thClass: 'table-header',
          tdClass: 'align-middle'
        }
      ],
      modalHiddenHandler: null
    }
  },
  computed: {
    isAddContactBulkTitle() {
      return ['addJanitors', 'addCraftsmen', 'addServiceProviders', 'addDelegate', 'addRevisor', 'addTenantChange', 'addOwner'].includes(this.title)
    },
    contactSearchFilters() {
      const filters = { isOnlyInterestedParty: false, active: true }
      const filteredOwners = [...new Set(this.ownerIds)]
      if (filteredOwners.length === 1 && this.accountId) {
        filters.ownerIds = [String(filteredOwners[0]), String(this.accountId)]
      } else if (filteredOwners.length > 1) {
        filters.ownerIds = [String(this.accountId)]
      }
      return filters
    },
    displayedSelectedContact() {
      let list = this.selectedContacts
      for (let i = 0; i < list.length; i++) {
        if (list[i]) {
          list[i]._showDetails = true
        }
      }
      return list
    }
  },
  watch: {
  },
  mounted() {
    this.modalHiddenHandler = (bvEvent, modalId) => {
      if (modalId === 'contact-bulk-modal') {
        this.selectedContacts = []
        this.name = ''
      }
    }
    this.$root.$on('bv::modal::hidden', this.modalHiddenHandler)
  },
  beforeDestroy() {
    if (this.modalHiddenHandler) {
      this.$root.$off('bv::modal::hidden', this.modalHiddenHandler)
    }
  },
  methods: {
    onChange(refine, name) {
      this.name = name
      refine(name)
    },
    onSelect(selected) {
      if (selected) {
        selected.internalNote = ''
        this.selectedContacts.push(selected.item)
      }
      this.name = ''
      this.$refs.autosuggest.internalValue = ''
    },
    hitsToSuggestions(hits) {
      return [{ data: hits }]
    },
    addTypeAction() {
      this.disabledBtn = true
      const contactIds = this.selectedContacts.map(c => c.objectID)
      let type = ''
      switch (this.title) {
        case 'addJanitors':
          type = 'BUILDING_CONTACT_TYPE_JANITOR'
          break
        case 'addCraftsmen':
          type = 'BUILDING_CONTACT_TYPE_CRAFTSMAN'
          break
        case 'addServiceProviders':
          type = 'BUILDING_CONTACT_TYPE_SERVICE_PROVIDER'
          break
        case 'addDelegate':
          type = 'BUILDING_CONTACT_TYPE_DELEGATE'
          break
        case 'addRevisor':
          type = 'BUILDING_CONTACT_TYPE_REVISOR'
          break
        case 'addTenantChange':
          type = 'BUILDING_CONTACT_TYPE_TENANT_CHANGE'
          break
        case 'addOwner':
          type = 'BUILDING_CONTACT_TYPE_OWNER'
          break
        default:
          break
      }

      let buildingContacts = []
      let contactsToRemove = []

      if (this.isAddContactBulkTitle && type) {
        const built = this.buildBuildingContactsForType(type, this.selectedContacts)
        buildingContacts = built.buildingContacts
        contactsToRemove = built.contactsToRemove
      } else {
        buildingContacts = this.selectedContacts.map(contact => ({
          id: contact.id || contact.objectID,
          note: contact.internalNote,
          type
        }))
      }

      const promises = []

      if (contactsToRemove.length > 0) {
        promises.push(ObjectApi.removeBuildingContacts(contactsToRemove, this.buildingIds))
      }

      if (buildingContacts.length > 0) {
        promises.push(ObjectApi.addBuildingContacts(buildingContacts, this.buildingIds))
      }

      if (promises.length === 0) {
        this.disabledBtn = false
        this.hide()
        return
      }

      Promise.all(promises)
        .then(() => {
          const adding = ['addJanitors', 'addCraftsmen', 'addServiceProviders', 'addDelegate', 'addRevisor', 'addTenantChange', 'addOwner'].includes(this.title)
          this.$emit('action-done', adding, contactIds, this.buildingIds)
          if (adding) {
            Vue.toasted.show(this.$t('message.manageBuilding.bulkAction.successMessages.' + this.title), { type: 'success' })
          } else {
            Vue.toasted.show(this.$t('message.accountSettings.updatedInformation'), { type: 'success' })
          }
        })
        .catch(e => {
          console.log(e)
          if (this.title === 'addJanitors') {
            Vue.toasted.show(this.$t('message.savingErrors.addJanitors'), { type: 'error' })
          } else {
            Vue.toasted.show(this.$t('message.savingErrors.building'), { type: 'error' })
          }
        })
        .finally(() => {
          this.disabledBtn = false
          this.hide()
        })
    },
    removeContactFromList(id) {
      this.selectedContacts.forEach((element, index) => {
        if (element.objectID === id) {
          this.selectedContacts.splice(index, 1)
        }
      }, this)
    },
    contactPersonChanged(contact, contactPersonId) {
      const oid = contact.objectID
      const selectedContact = this.selectedContacts.find(c =>
        (c.id && contact.id && c.id === contact.id) ||
        (oid && c.objectID === oid))
      if (selectedContact) {
        selectedContact.selectedContactPersonId = contactPersonId
      }
    },
    /**
     * Same rules as legacy tenant-change: plain contacts dedupe by id; companies can pick a contact person
     * and replace prior links for that company (company id or any of its persons) for this building-contact type.
     */
    buildBuildingContactsForType(type, selectedContacts) {
      const buildingContacts = []
      const contactsToRemove = []
      const existingIdsForType = new Set()

      this.existingContacts.forEach(contact => {
        if (contact.type === type) {
          existingIdsForType.add(contact.id)
        }
      })

      const processedCompanyIds = new Set()
      selectedContacts.forEach(contact => {
        const contactId = contact.id || contact.objectID

        if (!contact.contactPersons?.length) {
          if (!existingIdsForType.has(contactId)) {
            buildingContacts.push({
              id: contactId,
              note: contact.internalNote,
              type
            })
          }
          return
        }

        if (!processedCompanyIds.has(contactId)) {
          const selectedPersonId = contact.selectedContactPersonId || contactId

          if (existingIdsForType.has(selectedPersonId)) {
            processedCompanyIds.add(contactId)
            return
          }

          const existingCompanyContacts = this.existingContacts.filter(existingContact => {
            if (existingContact.type !== type) {
              return false
            }
            if (existingContact.id === contactId) {
              return true
            }
            return contact.contactPersons?.some(person => person.id === existingContact.id)
          })

          if (existingCompanyContacts.length > 0) {
            contactsToRemove.push(...existingCompanyContacts)
          }

          if (contact.selectedContactPersonId) {
            const selectedPerson = contact.contactPersons.find(cp => cp.id === contact.selectedContactPersonId)
            if (selectedPerson) {
              buildingContacts.push({
                id: selectedPerson.id,
                note: contact.internalNote,
                type
              })
            }
          } else {
            buildingContacts.push({
              id: contactId,
              note: contact.internalNote,
              type
            })
          }

          processedCompanyIds.add(contactId)
        }
      })

      return { buildingContacts, contactsToRemove }
    },
    noteChanged(id, note) {
      this.$emit('note-changed', id, note)
    },
    show() {
      this.$refs['contact-bulk-modal'].show()
    },
    hide() {
      this.$emit('on-close')
      this.selectedContacts = []
      this.$refs['contact-bulk-modal']?.hide()
    },
    getSuggestionValue(suggestion) {
      return suggestion.item.name
    }
  }
}
</script>

<style lang="scss" scoped>
  :deep(.custom-select) {
    background: none;
  }
  label {
    margin-bottom: 0;
  }
  .sizeLabel {
    font-size: 0.875rem !important;
  }

  .media-block {
    max-width: 33%;
    margin-right: 0.5%;

    .uploaded-file {
      background-position: center center;
      background-size: cover;
      min-height: 7em;
    }

    .file-block {
      min-height: 7em;
      background: $color-bg-grey-dark;

      a {
        color: $color-text-grey;
        overflow-wrap: break-word;
      }
    }
  }
</style>
